import { format, parseISO } from "date-fns";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import * as dashboardActions from "../../../redux/reducers/dashboardReducer";
import { dashboardState } from "../../../redux/reducers/dashboardReducer";

import defaultImage from "../../../assets/defaultImage.svg";
import greenlocation from "../../../assets/greenlocation.svg";
import greentime from "../../../assets/greentime.svg";
import {
  TwContainer,
  TwMainLayout,
  TwRadialProgress,
} from "../../../components/tailwind";
import TwProgressBar from "../../../components/tailwind/TwProgressBar";
import { useToast } from "../../../provider/ToastProvider";
import { setGlobalLoading } from "../../../redux/reducers/loadingReducer";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForEvent } from "../../../utils/confighelpers";
import SessionBooking from "../../businessProfile/view/SessionBooking";
import { useTranslation } from "react-i18next";

export default function SellersEventDetail() {
  const { sellerUserId } = useParams();
  const { t } = useTranslation();
  const { addSuccessToast } = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dashboardReduxState = useSelector(dashboardState);
  const [dataToDisplay, setDataToDisplay] = React.useState(dashboardReduxState.eventData || {});
  const [sessionDataToDisplay, setSessionDataToDisplay] = React.useState(
    dashboardReduxState.eventScheduleData
      ? {
          ...dashboardReduxState.eventScheduleData,
          eventPricingList: (dashboardReduxState.eventScheduleData?.eventPricingList || []).map((d) => ({
            ...d,
            textcolor: "text-primary",
            color: "bg-primary",
          })),
          addonsPricingList: (dashboardReduxState.eventScheduleData?.addonsPricingList || []).map((d) => ({
            ...d,
            capacity: d.capacity || 100,
            textcolor: "text-primary",
            color: "bg-primary",
          })),
        }
      : {}
  );

  const [bookingDetails, setBookingDetails] = React.useState({
    bookedPeople: [],
    seats: 0,
    seatsBooked: 0,
    seatsBookedPerc: 0,
    seatsEarning: 0,
    addons: 0,
    seatsBookedAddons: 0,
    seatsBookedAddonsPerc: 0,
  });

  const [metricsData, setMetricsData] = useState({});
  const [sellerCommission, setSellerCommission] = useState(null);
  const [copyText, setCopyText] = React.useState(t("myEvents.copy"));

  // Seller booking link
  const baseBookingLink = sessionDataToDisplay?.bookingLink || "";
  const sellerChannel = sessionDataToDisplay?.salesChannelList?.find(
    (ch) => ch.sellerId && ch.sellerId.toString() === sellerUserId && !ch.isDeleted
  );
  const sellerRefId = sellerChannel?.sellerReferenceId || "";
  const sellerBookingLink = sellerRefId
    ? baseBookingLink ? `${baseBookingLink}?ref=${sellerRefId}` : ""
    : baseBookingLink;

  // Compute booking details from metrics
  React.useEffect(() => {
    let ticketQty = 0;
    let ticketsBooked = 0;
    let addonQty = 0;
    let addonsBooked = 0;

    if (sessionDataToDisplay?.eventPricingList?.length > 0) {
      sessionDataToDisplay.eventPricingList.forEach((pricing) => {
        ticketQty += parseInt(pricing.capacity || 0);
        ticketsBooked += parseInt(pricing?.ticketsBooked?.ticketsBookedConfirmed || 0);
      });
      if (sessionDataToDisplay.addonsPricingList) {
        sessionDataToDisplay.addonsPricingList.forEach((addon) => {
          addonQty += parseInt(addon.capacity || 0);
          addonsBooked += parseInt(addon?.addonsBooked?.addonsBookedConfirmed || 0);
        });
      }
      // Override with seller-specific counts from metrics
      if (metricsData?.totalTicketsBooking !== undefined) {
        ticketsBooked = metricsData.totalTicketsBooking;
      }
      if (metricsData?.totalAddonsBooking !== undefined) {
        addonsBooked = metricsData.totalAddonsBooking;
      }
    }

    const seatsEarning = metricsData?.totalEarning !== undefined
      ? parseFloat(metricsData.totalEarning)
      : 0;

    setBookingDetails({
      bookedPeople: [],
      seats: ticketQty,
      seatsBooked: ticketsBooked,
      seatsBookedPerc: ticketQty ? parseFloat((ticketsBooked / ticketQty) * 100).toFixed(2) : 0,
      addons: addonQty,
      seatsBookedAddons: addonsBooked,
      seatsBookedAddonsPerc: addonQty ? parseFloat((addonsBooked / addonQty) * 100).toFixed(2) : 0,
      seatsEarning: parseFloat(seatsEarning).toFixed(2),
    });
  }, [sessionDataToDisplay, metricsData]);

  // Fetch event data + metrics
  React.useEffect(() => {
    (async () => {
      if (!dashboardReduxState.eventScheduleId) return;
      const isPckg = dashboardReduxState.eventScheduleData?.isPackage;
      const config = {
        method: "get",
        url: `${getUrlForEvent()}schedule/get?eventScheduleId=${dashboardReduxState.eventScheduleId}&isPackage=${isPckg}`,
      };
      dispatch(setGlobalLoading(true));
      const request = await axiosInstance(config);
      dispatch(setGlobalLoading(false));
      if (request.status === 200 && request.data.statusCode === 200) {
        dispatch(dashboardActions.setEventScheduleData(request.data.data));
        const scheduleData = {
          ...request.data.data,
          eventPricingList: (request.data.data.eventPricingList || []).map((d) => ({
            ...d,
            textcolor: "text-primary",
            color: "bg-primary",
          })),
          addonsPricingList: (request.data.data.addonsPricingList || []).map((d) => ({
            ...d,
            capacity: d.capacity || 100,
            textcolor: "text-primary",
            color: "bg-primary",
          })),
        };
        setSessionDataToDisplay(scheduleData);

        // Find seller commission
        if (scheduleData.salesChannelList) {
          const channel = scheduleData.salesChannelList.find(
            (ch) => ch.sellerId && ch.sellerId.toString() === sellerUserId && !ch.isDeleted
          );
          if (channel) setSellerCommission(channel);
        }
      }

      // Fetch seller-specific metrics
      await fetchMetrics();
    })();
  }, [dashboardReduxState.updateSessionComp]);

  const fetchMetrics = async () => {
    const config = {
      method: "get",
      url: `${getUrlForEvent()}getEventScheduleMetricsDetail?eventScheduleId=${dashboardReduxState.eventScheduleId}&sellerId=${sellerUserId}`,
    };
    dispatch(setGlobalLoading(true));
    const request = await axiosInstance(config);
    dispatch(setGlobalLoading(false));
    if (request.status === 200 && request.data.statusCode === 200) {
      if (request.data.data.salesEventMetrics?.length > 0) {
        const sellerMetrics = request.data.data.salesEventMetrics.find(
          (f) => f.sellerId && f.sellerId.toString() === sellerUserId
        ) || {};
        setMetricsData(sellerMetrics);
      }
    }
  };

  return (
    <TwMainLayout
      headTitle=""
      breadcrumbs={[
        { title: "Seller Events", onClick: () => navigate(`/seller/${sellerUserId}/events`) },
        { title: dataToDisplay.eventName || "" },
      ]}
      children={
        <TwContainer>
          <div className="flex flex-col gap-5 w-full">

            {/* Row 1: Event image (left) + info (right) — side by side */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div className="w-full h-44 md:h-72 flex justify-start">
                {dataToDisplay.eventPhoto ? (
                  <img
                    className="w-full sm:h-60 lg:h-72 object-cover rounded-2xl border border-gray-200"
                    src={dataToDisplay.eventPhoto}
                    alt=""
                  />
                ) : (
                  <img
                    className="w-full sm:h-60 lg:h-72 object-cover rounded-2xl"
                    src={defaultImage}
                    alt=""
                  />
                )}
              </div>
              <div className="flex flex-col gap-3 justify-center">
                <div>
                  <h2 className="font-bold text-xl md:text-3xl">{dataToDisplay.eventName}</h2>
                  <p className="text-sm md:text-base">{dataToDisplay.tagLines}</p>
                </div>
                <div className="flex items-center">
                  <div className="flex-none">
                    <img src={greentime} alt="" />
                  </div>
                  <div className="flex-1 pl-2">
                    <div className="text-gray-500 text-sm">{t("myEvents.eventDate")}</div>
                    <div className="text-black text-base">
                      {sessionDataToDisplay.eventDate
                        ? format(parseISO(sessionDataToDisplay.eventDate), "EEEE, MMMM d 'at' HH:mm") +
                          " - " +
                          format(parseISO(sessionDataToDisplay?.eventEndDate), "HH:mm")
                        : ""}
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="flex-none">
                    <img src={greenlocation} alt="" />
                  </div>
                  <div className="flex-1 pl-2">
                    <div className="text-gray-500 text-sm">{t("myEvents.venue")}</div>
                    <div className="text-black text-base">
                      <p className="font-bold">
                        {sessionDataToDisplay?.location?.addressName || ""}
                      </p>
                      <p className="line-clamp-2">
                        {sessionDataToDisplay?.location?.address || ""}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Row 2: Booking link — full width */}
            {sellerBookingLink && (
              <div className="border-2 border-gray-200 rounded-xl w-full">
                <div className="flex justify-between border-b-2 border-bright-gray p-5">
                  <h3 className="text-black text-lg font-semibold">
                    {t("myEvents.bookingLink")}
                  </h3>
                  <button
                    className="text-sm text-primary font-semibold"
                    onClick={() => window.open(sellerBookingLink, "_blank")}
                  >
                    {t("myEvents.preview")}&nbsp;↗
                  </button>
                </div>
                <div className="flex flex-col gap-3 p-5">
                  <input
                    readOnly
                    type="text"
                    value={sellerBookingLink}
                    className="block w-full rounded-md bg-white px-4 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 sm:text-sm/6"
                    onFocus={async (e) => {
                      e.target.select();
                      try {
                        await navigator.clipboard.writeText(sellerBookingLink);
                        addSuccessToast(t("myEvents.copied"));
                      } catch (err) {}
                    }}
                  />
                  <button
                    type="button"
                    className="rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 ring-0 ring-inset ring-gray-300 hover:bg-gray-50"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(sellerBookingLink);
                        setCopyText(t("myEvents.copied"));
                        setTimeout(() => setCopyText(t("myEvents.copy")), 3000);
                        addSuccessToast(t("myEvents.copied"));
                      } catch (err) {}
                    }}
                  >
                    <span className="text-sm font-semibold text-black">{copyText}</span>
                  </button>
                </div>
              </div>
            )}

            {/* Row 3: Metrics cards — full width */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="border border-gray-200 rounded-xl p-4">
                <div className="text-sm text-gray-500">{t("myEvents.orders")}</div>
                <div className="text-2xl font-bold">{metricsData?.totalBooking || 0}</div>
              </div>
              <div className="border border-gray-200 rounded-xl p-4">
                <div className="text-sm text-gray-500">{t("myEvents.tickets")}</div>
                <div className="text-2xl font-bold">{bookingDetails.seatsBooked}</div>
              </div>
              <div className="border border-gray-200 rounded-xl p-4">
                <div className="text-sm text-gray-500">{t("myEvents.addOns")}</div>
                <div className="text-2xl font-bold">{bookingDetails.seatsBookedAddons}</div>
              </div>
              <div className="border border-gray-200 rounded-xl p-4">
                <div className="text-sm text-gray-500">{t("myEvents.revenue")}</div>
                <div className="text-2xl font-bold">€{bookingDetails.seatsEarning}</div>
              </div>
            </div>

            {/* Row 4: Ticket progress bars — full width */}
            {sessionDataToDisplay?.eventPricingList?.length > 0 && (
              <div className="border border-gray-200 rounded-xl p-5">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-lg font-semibold">{t("myEvents.tickets")}</h4>
                  <div className="flex items-center gap-2">
                    <TwRadialProgress progress={bookingDetails.seatsBookedPerc} className="text-primary" />
                    <span className="text-sm font-semibold">
                      {bookingDetails.seatsBooked}/{bookingDetails.seats}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  {sessionDataToDisplay.eventPricingList.map((item, i) => {
                    const sellerTicketData = metricsData?.eventPricingList?.find(
                      (epl) => epl.ticketType === item.ticketType
                    );
                    const booked = sellerTicketData
                      ? sellerTicketData.ticketsBookedConfirmed || 0
                      : item.ticketsBooked?.ticketsBookedConfirmed || 0;
                    const cap = item.capacity || 0;
                    const percent = cap ? parseFloat((booked / cap) * 100).toFixed(0) : 0;
                    return (
                      <div key={i} className="flex flex-col w-full">
                        <div className="text-sm text-gray-800 flex gap-1">
                          <span className="line-clamp-1 font-bold">{item.ticketType}</span>
                          <span className="text-gray-400">({percent}%)</span>
                          <span className="text-gray-400">€{item.amountForTicket}</span>
                        </div>
                        <div className="flex justify-between items-center gap-1">
                          <TwProgressBar value={percent} color={item.color} max={100} />
                          <div className="text-sm font-semibold text-gray-900 w-12 text-right">
                            {booked}/{cap}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Row 5: Addon progress bars — full width */}
            {sessionDataToDisplay?.addonsPricingList?.length > 0 && (
              <div className="border border-gray-200 rounded-xl p-5">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-lg font-semibold">{t("myEvents.addOns")}</h4>
                  <div className="flex items-center gap-2">
                    <TwRadialProgress progress={bookingDetails.seatsBookedAddonsPerc} className="text-primary" />
                    <span className="text-sm font-semibold">
                      {bookingDetails.seatsBookedAddons}/{bookingDetails.addons}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  {sessionDataToDisplay.addonsPricingList.map((item, i) => {
                    const sellerAddonData = metricsData?.addonsPricingList?.find(
                      (epl) => epl.id === item.id || epl.addonName === item.addonName
                    );
                    const booked = sellerAddonData
                      ? sellerAddonData.addonsBookedConfirmed || 0
                      : item.addonsBooked?.addonsBookedConfirmed || 0;
                    const cap = item.capacity || 0;
                    const percent = cap ? parseFloat((booked / cap) * 100).toFixed(0) : 0;
                    return (
                      <div key={i} className="flex flex-col w-full">
                        <div className="text-sm text-gray-800 flex gap-1">
                          <span className="line-clamp-1 font-bold">{item.addonName}</span>
                          <span className="text-gray-400">({percent}%)</span>
                        </div>
                        <div className="flex justify-between items-center gap-1">
                          <TwProgressBar value={percent} color={item.color} max={100} />
                          <div className="text-sm font-semibold text-gray-900 w-12 text-right">
                            {booked}/{cap}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Row 6: Commission — full width */}
            {sellerCommission?.isCommissionApplicable && (
              <div className="border-2 border-gray-200 rounded-xl w-full">
                <div className="flex justify-between border-b-2 border-bright-gray p-5">
                  <h3 className="text-black text-lg font-semibold">Commission</h3>
                </div>
                <div className="p-5">
                  <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-sm text-gray-500">Model</div>
                      <div className="text-sm font-semibold capitalize">
                        {sellerCommission.commissionModelType || "per-ticket"}
                      </div>
                      <div className="text-sm text-gray-500">Unit</div>
                      <div className="text-sm font-semibold capitalize">
                        {sellerCommission.unitType || "fixed"}
                      </div>
                    </div>
                    {sellerCommission.tiers?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">
                          Commission Tiers
                        </h4>
                        <table className="min-w-full divide-y divide-gray-300">
                          <thead>
                            <tr>
                              <th className="py-2 text-left text-sm font-semibold text-gray-900">From</th>
                              <th className="py-2 text-left text-sm font-semibold text-gray-900">To</th>
                              <th className="py-2 text-left text-sm font-semibold text-gray-900">
                                {sellerCommission.unitType === "percentage" ? "%" : "€"} / Ticket
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {sellerCommission.tiers.map((tier, i) => (
                              <tr key={i}>
                                <td className="py-2 text-sm">{tier.from}</td>
                                <td className="py-2 text-sm">{tier.to || "∞"}</td>
                                <td className="py-2 text-sm">
                                  {sellerCommission.unitType === "percentage"
                                    ? `${tier.commissionPerTicket}%`
                                    : `€${tier.commissionPerTicket}`}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {metricsData?.calculateSalesCommission?.finalCommissionWithBonusApplied !== undefined && (
                      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                        <div className="text-sm text-gray-500">Earned Commission</div>
                        <div className="text-2xl font-bold text-green-700">
                          €{parseFloat(metricsData.calculateSalesCommission.finalCommissionWithBonusApplied || 0).toFixed(2)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Row 7: Orders — full width, filtered to this seller */}
            <div>
              <SessionBooking
                sessionDataToDisplay={sessionDataToDisplay}
                displayHeader={false}
                metricsFilterList={[
                  {
                    value: sellerUserId,
                    title: sellerChannel?.name || "This Seller",
                    data: metricsData,
                  },
                ]}
              />
            </div>
          </div>
        </TwContainer>
      }
    />
  );
}
